import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Check, Lightbulb, Layers, Edit3, Trophy, ChevronRight, Search, Target, LifeBuoy, SkipForward, Bot, Sparkles, Briefcase, Brain } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { StepRenderer } from '../components/app/StepRenderer';
import { SlotMachine } from '../components/app/SlotMachine';
import { AICoachPanel } from '../components/app/AICoachPanel';
import { day1Content, day2Placeholder } from '../data/day1Content';
import { day2Content } from '../data/day2Content';
import { day3Content } from '../data/day3Content';
import { day4Content } from '../data/day4Content';
import { day5Content } from '../data/day5Content';
import { day6Content } from '../data/day6Content';
import { day7Content } from '../data/day7Content';
import { day8Content } from '../data/day8Content';
import { featureFlags } from '../config/featureFlags';

// ═══════════════════════════════════════════════════════════════
// NOWA STRONA LEKCJI - Dzień 1 w pełni zaimplementowany
// Premium Motion Language + 40 kroków interakcji + Koła ratunkowe
// ═══════════════════════════════════════════════════════════════

export const NewLessonPage = () => {
  const { user, api, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dayNumber = parseInt(location.pathname.split('/').pop());
  
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentExercise, setCurrentExercise] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [showSlotMachine, setShowSlotMachine] = useState(false);
  const [dayCompleted, setDayCompleted] = useState(false);
  const [exerciseTransition, setExerciseTransition] = useState(false);
  const [showAICoach, setShowAICoach] = useState(false);
  
  // Lifelines state
  const [lifelines, setLifelines] = useState(4);
  const [lifelineAnimating, setLifelineAnimating] = useState(false);
  
  // Step transition animation state
  const [stepTransition, setStepTransition] = useState('enter'); // 'enter' | 'exit' | 'idle'
  const [pendingStep, setPendingStep] = useState(null);
  
  // Progress restored flag
  const [progressRestored, setProgressRestored] = useState(false);

  // Icons mapping - rozszerzony o nowe ikony
  const iconMap = {
    lightbulb: Lightbulb,
    layers: Layers,
    edit: Edit3,
    trophy: Trophy,
    search: Search,
    target: Target,
    repeat: Lightbulb, // For iteration icon
    link: Layers, // For chain-of-thought
    copy: Edit3, // For few-shot
    'x-circle': Search, // For negative prompting
    sparkles: Sparkles, // For AI Coach intro
    briefcase: Briefcase, // For work exercises
    brain: Brain, // For thinking exercises
    bot: Bot // For AI related
  };

  useEffect(() => {
    // Load lesson content
    const contentMap = { 1: day1Content, 2: day2Content, 3: day3Content, 4: day4Content, 5: day5Content, 6: day6Content, 7: day7Content, 8: day8Content };
    const content = contentMap[dayNumber];
    if (content) setLesson(content);
    
    // Load lifelines and saved progress in parallel
    const loadData = async () => {
      try {
        const [lifelineRes, progressRes] = await Promise.all([
          api.get(`/lessons/lifelines/${dayNumber}`).catch(() => ({ data: { remaining: 2 } })),
          api.get(`/lessons/progress/${dayNumber}`).catch(() => ({ data: { has_progress: false } }))
        ]);
        
        setLifelines(lifelineRes.data.remaining);
        
        // Restore saved progress if available
        if (progressRes.data.has_progress) {
          setCurrentExercise(progressRes.data.current_exercise);
          setCurrentStep(progressRes.data.current_step);
          setCompletedSteps(progressRes.data.completed_steps || []);
        }
        setProgressRestored(true);
      } catch (e) {
        console.error('Failed to load lesson data:', e);
        setProgressRestored(true);
      }
    };
    
    loadData();
    setLoading(false);
    
    // Initialize step transition to idle after first render
    setTimeout(() => setStepTransition('idle'), 300);
    
    // Listen for AI Coach open event from StepRenderer
    const handleOpenAICoach = () => {
      if (dayNumber >= 4) {
        setShowAICoach(true);
      }
    };
    window.addEventListener('openAICoach', handleOpenAICoach);
    
    return () => {
      window.removeEventListener('openAICoach', handleOpenAICoach);
    };
  }, [dayNumber, api]);

  // Save progress on page exit (beforeunload / navigation away)
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (!dayCompleted && progressRestored && completedSteps.length > 0) {
        // Use sendBeacon for reliable save on page close
        const data = JSON.stringify({
          day_number: dayNumber,
          current_exercise: currentExercise,
          current_step: currentStep,
          completed_steps: completedSteps,
        });
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/lessons/progress`;
        const token = localStorage.getItem('token');
        const blob = new Blob([data], { type: 'application/json' });
        // sendBeacon doesn't support auth headers, fallback to fetch keepalive
        fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: data,
          keepalive: true,
        }).catch(() => {});
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [dayNumber, currentExercise, currentStep, completedSteps, dayCompleted, progressRestored]);

  // Calculate progress
  const totalSteps = lesson?.exercises?.reduce((acc, ex) => acc + (ex.steps?.length || 0), 0) || 0;
  const completedCount = completedSteps.length;
  const progressPercent = totalSteps > 0 ? (completedCount / totalSteps) * 100 : 0;

  const currentExerciseData = lesson?.exercises?.[currentExercise];
  const currentStepData = currentExerciseData?.steps?.[currentStep];
  const ExerciseIcon = iconMap[currentExerciseData?.icon] || Lightbulb;

  // Save progress and navigate back
  const handleBack = () => {
    if (completedSteps.length > 0 && !dayCompleted) {
      saveProgress(currentExercise, currentStep, completedSteps);
    }
    navigate('/lessons');
  };
  const saveProgress = useCallback((exercise, step, steps) => {
    api.post('/lessons/progress', {
      day_number: dayNumber,
      current_exercise: exercise,
      current_step: step,
      completed_steps: steps,
    }).catch(() => {}); // Silent fail — not critical
  }, [api, dayNumber]);

  // Handle step completion with smooth transitions
  const handleStepComplete = async (result) => {
    const stepId = currentStepData?.id;
    const newCompleted = completedSteps.includes(stepId) 
      ? completedSteps 
      : [...completedSteps, stepId];
    
    if (!completedSteps.includes(stepId)) {
      setCompletedSteps(newCompleted);
    }

    // Move to next step or exercise
    const exerciseSteps = currentExerciseData?.steps || [];
    
    if (currentStep < exerciseSteps.length - 1) {
      // Next step in same exercise - smooth fade transition
      const nextStep = currentStep + 1;
      saveProgress(currentExercise, nextStep, newCompleted);
      
      // Phase 1: Fade out (250ms)
      setStepTransition('exit');
      
      // Phase 2: Change step after fade out completes, then fade in
      setTimeout(() => {
        setCurrentStep(nextStep);
        // Small delay to ensure React renders new content before fade in
        requestAnimationFrame(() => {
          setStepTransition('idle');
        });
      }, 280); // Slightly longer than animation to ensure completion
    } else if (currentExercise < (lesson?.exercises?.length || 0) - 1) {
      // Next exercise - show transition
      const nextExercise = currentExercise + 1;
      saveProgress(nextExercise, 0, newCompleted);
      
      setExerciseTransition(true);
      toast.success(`Ćwiczenie "${currentExerciseData?.title}" ukończone!`);
      setTimeout(() => {
        setCurrentExercise(nextExercise);
        setCurrentStep(0);
        setExerciseTransition(false);
      }, 1200);
    } else {
      // Day completed!
      handleDayComplete();
    }
  };

  const handleDayComplete = async () => {
    setDayCompleted(true);
    
    try {
      await api.post('/lessons/complete', { day_number: dayNumber });
      // Clear partial progress since day is done
      api.delete(`/lessons/progress/${dayNumber}`).catch(() => {});
      await refreshUser();
    } catch (e) {
      console.error(e);
    }

    // Show slot machine for Day 1
    if (dayNumber === 1 && featureFlags.slotMachineEnabled) {
      setTimeout(() => setShowSlotMachine(true), 1500);
    }
  };

  const handleSlotMachineClose = () => {
    setShowSlotMachine(false);
    navigate('/lessons');
  };

  const handleSlotMachineWin = async () => {
    try {
      await api.post('/lottery/claim-free-month');
      await refreshUser();
      toast.success('🎉 Wygrałeś 24h darmowego dostępu PRO!');
    } catch (e) {
      console.error('Failed to claim free access:', e);
      toast.error('Nie udało się odebrać nagrody');
    }
  };

  // Handle skip with lifeline
  const handleSkipWithLifeline = async () => {
    if (lifelines <= 0 || lifelineAnimating) return;
    
    setLifelineAnimating(true);
    
    try {
      const res = await api.post('/lessons/use-lifeline', { day_number: dayNumber });
      setLifelines(res.data.remaining);
      
      toast.success('🛟 Pominięto pytanie!', { duration: 4000 });
      
      // Skip to next step
      const stepId = currentStepData?.id;
      if (!completedSteps.includes(stepId)) {
        setCompletedSteps(prev => [...prev, stepId]);
      }
      
      const exerciseSteps = currentExerciseData?.steps || [];
      
      // Animate step exit then transition - smooth fade
      setStepTransition('exit');
      setTimeout(() => {
        if (currentStep < exerciseSteps.length - 1) {
          setCurrentStep(prev => prev + 1);
          requestAnimationFrame(() => {
            setStepTransition('idle');
            setLifelineAnimating(false);
          });
        } else if (currentExercise < (lesson?.exercises?.length || 0) - 1) {
          setExerciseTransition(true);
          setTimeout(() => {
            setCurrentExercise(prev => prev + 1);
            setCurrentStep(0);
            setExerciseTransition(false);
            requestAnimationFrame(() => {
              setStepTransition('idle');
              setLifelineAnimating(false);
            });
          }, 800);
        } else {
          handleDayComplete();
          setLifelineAnimating(false);
        }
      }, 300);
      
    } catch (e) {
      toast.error('Nie można pominąć pytania');
      setLifelineAnimating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'transparent' }}>
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-sky-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-400 animate-pulse">Ładowanie lekcji...</p>
        </div>
      </div>
    );
  }

  // Placeholder for Days 2, 3
  if (lesson?.status === 'placeholder') {
    return (
      <div className="min-h-screen pb-24" style={{ background: 'transparent' }}>
        <div className="sticky top-0 px-6 py-4 z-20" style={{ background: 'rgba(10, 20, 35, 0.9)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <button onClick={() => navigate('/lessons')} className="flex items-center text-slate-400 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5 mr-2" /> Wróć
          </button>
        </div>
        
        <div className="px-6 py-8">
          <Card className="mb-6 border-0" style={{ background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(12px)' }}>
            <CardContent className="pt-6 text-center">
              <div className="w-16 h-16 rounded-full bg-slate-700 flex items-center justify-center mx-auto mb-4">
                <Layers className="w-8 h-8 text-slate-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">{lesson.title}</h2>
              <p className="text-slate-400 mb-4">{lesson.subtitle}</p>
              <p className="text-sm text-slate-500 mb-6">
                Skompresowane z: {lesson.compressed_from}
              </p>
              
              <div className="rounded-xl p-4 text-left" style={{ background: 'rgba(245, 158, 11, 0.15)', border: '1px solid rgba(245, 158, 11, 0.3)' }}>
                <p className="font-medium text-amber-400 mb-2">Cele tego dnia:</p>
                <ul className="space-y-1">
                  {lesson.goals?.map((goal, idx) => (
                    <li key={idx} className="text-sm text-amber-300/80 flex items-start gap-2">
                      <ChevronRight className="w-4 h-4 mt-0.5 shrink-0" />
                      {goal}
                    </li>
                  ))}
                </ul>
              </div>
              
              <p className="text-sm text-slate-500 mt-6">
                🚧 Ta lekcja będzie dostępna wkrótce
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Day completed screen with celebration
  if (dayCompleted && !showSlotMachine) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: 'transparent' }}>
        <Card className="max-w-sm w-full card-enter border-0" style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(16px)' }}>
          <CardContent className="pt-8 text-center">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center mx-auto mb-6 animate-celebrate shadow-lg shadow-emerald-500/30">
              <Trophy className="w-12 h-12 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Dzień {dayNumber} ukończony! 🎉</h2>
            <p className="text-slate-300 mb-2">
              Świetna robota! Ukończyłeś wszystkie {lesson?.exercises?.length} ćwiczeń.
            </p>
            <p className="text-sm text-slate-400 mb-6">
              To było {totalSteps} kroków interaktywnej nauki!
            </p>
            <div className="space-y-3">
              <Button 
                onClick={() => navigate('/lessons')} 
                className="w-full bg-sky-500 hover:bg-sky-600 rounded-xl h-12 text-base font-medium btn-press"
                data-testid="back-to-lessons-btn"
              >
                Wróć do lekcji
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-32" style={{ background: 'transparent' }}>
      {/* Header with animated progress */}
      <div className="sticky top-0 px-6 py-4 z-20" style={{ background: 'rgba(10, 20, 35, 0.9)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <div className="flex items-center justify-between mb-3">
          <button 
            onClick={handleBack} 
            className="flex items-center text-slate-400 hover:text-white transition-colors active:scale-95"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5 mr-2" /> Wróć
          </button>
          
          {/* Lifeline counter */}
          <div className="flex items-center gap-3">
            <div 
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all duration-300 ${
                lifelines > 0 
                  ? 'bg-amber-500/20 border border-amber-500/30' 
                  : 'bg-slate-700 border border-slate-600'
              } ${lifelineAnimating ? 'scale-95' : ''}`}
              data-testid="lifeline-counter"
            >
              <span className="text-lg">🛟</span>
              <span className={`text-sm font-bold ${lifelines > 0 ? 'text-amber-400' : 'text-slate-500'}`}>
                {lifelines}
              </span>
            </div>
            <span className="text-sm text-slate-400 font-medium">Dzień {lesson?.day_number}</span>
          </div>
        </div>
        
        {/* Animated Progress bar */}
        <div className="flex items-center gap-3">
          <div className="flex-1 h-2.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.1)' }}>
            <div 
              className="h-full bg-gradient-to-r from-sky-500 to-emerald-500 rounded-full transition-all duration-700 ease-out"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <span className="text-xs text-slate-400 font-semibold w-12 text-right">{Math.round(progressPercent)}%</span>
        </div>
      </div>

      <div className="px-4 py-4">
        {/* Day title */}
        <div className="text-center mb-6">
          <h1 className="text-xl font-bold text-white">{lesson?.title}</h1>
          <p className="text-sm text-slate-400">{lesson?.subtitle}</p>
          <p className="text-xs text-slate-500 mt-1">⏱️ {lesson?.duration}</p>
        </div>

        {/* Exercise header with transition */}
        {currentExerciseData && (
          <Card className={`mb-4 border-0 transition-all duration-500 ${exerciseTransition ? 'opacity-50 scale-98' : 'opacity-100 scale-100'}`} style={{ background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.15), rgba(16, 185, 129, 0.15))', backdropFilter: 'blur(8px)' }}>
            <CardContent className="py-3">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-sky-500 to-emerald-500 flex items-center justify-center shadow-sm">
                  <ExerciseIcon className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-sky-400 font-medium">
                    Ćwiczenie {currentExercise + 1} z {lesson?.exercises?.length}
                  </p>
                  <p className="font-semibold text-white">{currentExerciseData.title}</p>
                </div>
                <div className="text-right">
                  <span className="text-xs text-sky-300 font-semibold bg-sky-500/20 px-2.5 py-1 rounded-full">
                    {currentStep + 1}/{currentExerciseData.steps?.length}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Current step with premium micro-animation transitions */}
        {currentStepData && !exerciseTransition && (
          <>
            <div 
              className="step-transition-container"
              style={{
                minHeight: '420px', // Fixed height prevents layout shift
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <div
                key={`step-${currentStep}-${currentExercise}`} // Force re-render for animation
                style={{
                  opacity: stepTransition === 'exit' ? 0 : 1,
                  transform: stepTransition === 'exit' 
                    ? 'translateY(-8px)' 
                    : 'translateY(0px)',
                  transition: 'opacity 250ms ease-in-out, transform 250ms ease-in-out',
                  willChange: 'opacity, transform'
                }}
              >
                <StepRenderer
                  step={currentStepData}
                  onComplete={handleStepComplete}
                  stepNumber={currentStep + 1}
                  totalSteps={currentExerciseData?.steps?.length || 0}
                />
              </div>
            </div>
            
            {/* Skip button with lifeline */}
            <div className="mt-4 flex justify-center">
              <button
                onClick={handleSkipWithLifeline}
                disabled={lifelines <= 0 || lifelineAnimating}
                className={`
                  flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium
                  transition-all duration-200
                  ${lifelines > 0 && !lifelineAnimating
                    ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30 active:scale-95 cursor-pointer'
                    : 'bg-slate-700 text-slate-500 border border-slate-600 cursor-not-allowed'
                  }
                `}
                data-testid="skip-btn"
              >
                <SkipForward className="w-4 h-4" />
                <span>Pomiń pytanie</span>
                <span className="text-lg">🛟</span>
              </button>
            </div>
          </>
        )}
        
        {/* Exercise transition overlay */}
        {exerciseTransition && (
          <div className="flex items-center justify-center py-20">
            <div className="text-center animate-scaleIn">
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-emerald-400" />
              </div>
              <p className="text-slate-300 font-medium">Ładowanie następnego ćwiczenia...</p>
            </div>
          </div>
        )}
      </div>

      {/* Slot Machine Modal */}
      {showSlotMachine && (
        <SlotMachine
          onClose={handleSlotMachineClose}
          onWin={handleSlotMachineWin}
        />
      )}

      {/* AI Coach Modal */}
      {showAICoach && (
        <AICoachPanel
          onClose={() => setShowAICoach(false)}
          dayNumber={dayNumber}
          lessonContext={lesson?.title}
        />
      )}

      {/* AI Coach FAB - visible on Day 4+ */}
      {dayNumber >= 4 && !showAICoach && !dayCompleted && (
        <button
          onClick={() => setShowAICoach(true)}
          className="fixed bottom-24 right-4 z-30 w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-105 active:scale-95"
          style={{
            background: 'linear-gradient(135deg, #0ea5e9, #10b981)',
            boxShadow: '0 8px 25px rgba(14, 165, 233, 0.4)'
          }}
          data-testid="ai-coach-fab"
        >
          <Bot className="w-6 h-6 text-white" />
        </button>
      )}

      {/* Notes button - DISABLED */}
      {featureFlags.notesEnabled && (
        <div className="fixed bottom-20 left-4 z-30">
          {/* Notes would be here */}
        </div>
      )}
    </div>
  );
};

export default NewLessonPage;
